import type { RequestFn } from '../http';
import type {
  AcceptanceRecipient,
  RemoteApprovalStatus,
  Submission,
  SubmissionUpdate,
} from '../types';
import type { WeclappTicketListResponse } from '../types/weclapp';

export type DeliveryReportMode = 'CW_ONLY' | 'WECLAPP_ONLY';

/** Einsatz-Archiv: abgeschlossene Berichte, für alle Mitarbeitenden lesbar. */
export type ArchiveType = 'all' | 'service' | 'delivery';

export interface ArchiveListItem {
  id: string;
  formType: 'ONSITE_SERVICE' | 'ONSITE_DELIVERY';
  customerName: string | null;
  ticketNumber: string | null;
  ticketSummary: string | null;
  technician: { id: string; displayName: string } | null;
  /** Belegnummer des Berichts — SB-… beim Servicebericht, LN-… beim Liefernachweis. */
  documentNumber: string | null;
  result: string | null;
  itemCount: number;
  hasSignature: boolean;
  hasPdf: boolean;
  submittedAt: string | null;
  /** Stand der Asset-Übernahme ins Ticketsystem (nur Liefernachweise). */
  assetSync: { total: number; success: number; failed: number; pending: number };
}

/**
 * Das Beleg-PDF als Blob.
 *
 * Für die Vorschau im Archiv der einzige Weg: Die API schickt auf jeder Antwort
 * `X-Frame-Options: DENY` und `frame-ancestors 'none'` — ein `<iframe>` direkt auf
 * `/api/…/pdf` bleibt deshalb leer. Eine `blob:`-URL trägt diese Kopfzeilen nicht.
 */
async function fetchSubmissionPdfBlob(id: string): Promise<Blob> {
  const res = await fetch(`/api/submissions/${id}/pdf`, { credentials: 'include' });
  if (!res.ok) {
    let msg = 'PDF konnte nicht geladen werden';
    try {
      const j = (await res.json()) as { error?: string };
      msg = j.error ?? msg;
    } catch {
      /* ignore */
    }
    throw new Error(msg);
  }
  return res.blob();
}

export function createSubmissionsApi(request: RequestFn) {
  return {
    /** Abgeschlossene Berichte aller Techniker — Liste im Einsatz-Archiv. */
    async getSubmissionArchive(params: {
      type?: ArchiveType;
      q?: string;
      limit?: number;
      offset?: number;
    } = {}) {
      const qs = new URLSearchParams();
      if (params.type && params.type !== 'all') qs.set('type', params.type);
      if (params.q) qs.set('q', params.q);
      if (params.limit != null) qs.set('limit', String(params.limit));
      if (params.offset != null) qs.set('offset', String(params.offset));
      const query = qs.toString();
      return request<{
        records: ArchiveListItem[];
        total: number;
        limit: number;
        offset: number;
      }>(`/submissions/archive${query ? `?${query}` : ''}`);
    },

    /** Rückgabe-Hinweis als erledigt markieren (nach Überarbeitung). */
    async handleSubmissionReturn(id: string) {
      return request<{ submission: Submission }>(`/submissions/${id}/handle-return`, {
        method: 'POST',
        body: JSON.stringify({}),
      });
    },

    /**
     * Bereits übertragenen Bericht korrigieren: Kopie als neuer Entwurf, Original
     * wird SUPERSEDED, alte Weclapp-Buchungen/Dokumente werden zurückgenommen.
     */
    async correctSubmission(id: string, reason: string) {
      return request<{
        correction: { id: string; formType: 'ONSITE_SERVICE' | 'ONSITE_DELIVERY' };
        original: { id: string; status: string };
        cleanup: { kind: string; id: string; result: string; error?: string }[];
        noteWritten: boolean;
      }>(`/submissions/${id}/correct`, {
        method: 'POST',
        body: JSON.stringify({ reason }),
      });
    },

    /**
     * Liefernachweis aus einem Servicebericht: Ticket, Kunde und Material
     * werden übernommen. Ein offener Lieferschein aus demselben Bericht
     * kommt zurück statt eines zweiten Entwurfs.
     */
    async createDeliveryFromService(id: string) {
      return request<{
        delivery: { id: string; formType: 'ONSITE_DELIVERY' };
        reused: boolean;
        finalized?: boolean;
        itemCount: number;
      }>(`/submissions/${id}/create-delivery`, {
        method: 'POST',
        body: JSON.stringify({}),
      });
    },

    /** Eigenen Bericht mit Begründung stornieren. */
    async cancelOwnSubmission(id: string, reason: string) {
      return request<{ submission: Submission }>(`/submissions/${id}/cancel`, {
        method: 'POST',
        body: JSON.stringify({ reason }),
      });
    },

    async getSubmissions(params?: { status?: string; formType?: string; scope?: 'team' }) {
      const query = params
        ? '?' + new URLSearchParams(params as Record<string, string>).toString()
        : '';
      return request<{ submissions: Submission[] }>(`/submissions${query}`);
    },

    async getOpenSubmissions() {
      return request<{ submissions: Submission[] }>('/submissions/open-drafts');
    },

    async getSubmission(id: string) {
      return request<{ submission: Submission }>(`/submissions/${id}`);
    },

    async createSubmission(formType: 'ONSITE_SERVICE' | 'ONSITE_DELIVERY') {
      return request<{ submission: Submission }>('/submissions', {
        method: 'POST',
        body: JSON.stringify({ formType }),
      });
    },

    async updateSubmission(id: string, data: Partial<SubmissionUpdate>) {
      return request<{ submission: Submission }>(`/submissions/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
        // Nur den neuen Stand zurück, nicht den ganzen Bericht samt Fotos (bis 10 MB je
        // Speichern). Gelesen wird aus der Antwort ohnehin nur `updatedAt` (submissionSaveLock).
        headers: { Prefer: 'return=minimal' },
      });
    },

    /**
     * Abnahme per E-Mail: Vorschläge für den Ansprechpartner aus den Stammdaten
     * (Portal-Kontakte, gepflegte Mitarbeiter, Melder des Tickets).
     */
    async getAcceptanceRecipients(id: string) {
      return request<{ recipients: AcceptanceRecipient[] }>(`/submissions/${id}/acceptance-recipients`);
    },

    async getRemoteApproval(id: string) {
      return request<{ approval: RemoteApprovalStatus | null; ttlDays: number }>(
        `/submissions/${id}/remote-approval`
      );
    },

    /** Kurzfassung mit Abnahme-Link an den Ansprechpartner schicken. */
    async requestRemoteApproval(
      id: string,
      data: {
        recipientName: string;
        recipientEmail: string;
        recipientSource?: 'CONTACT' | 'EMPLOYEE' | 'TICKET' | 'MANUAL';
        note?: string;
      }
    ) {
      // `submission.updatedAt`: Stand nach dem Schreiben des Servers — für die Sperre des Formulars.
      return request<{ success: boolean; approval: RemoteApprovalStatus | null; submission?: { id: string; updatedAt: string } | null }>(
        `/submissions/${id}/remote-approval`,
        { method: 'POST', body: JSON.stringify(data) }
      );
    },

    async finalizeSubmission(id: string) {
      return request<{ success: boolean; message: string; deliveryId?: string }>(
        `/submissions/${id}/finalize`,
        { method: 'POST' }
      );
    },

    /** Fehlgeschlagenem Bericht ohne Ticket ein weclapp-Ticket zuordnen und neu übertragen. */
    async assignSubmissionTicket(id: string, ticketId: string) {
      return request<{ success: boolean; ticketId: string; ticketNumber: string | null }>(
        `/submissions/${id}/assign-ticket`,
        { method: 'POST', body: JSON.stringify({ ticketId }) }
      );
    },

    async deleteSubmission(id: string) {
      return request<{ success: boolean; message: string }>(`/submissions/${id}`, {
        method: 'DELETE',
      });
    },

    async bulkDeleteSubmissions(ids: string[]) {
      return request<{ success: boolean; deleted: number; message: string }>(
        '/submissions/bulk-delete',
        {
          method: 'POST',
          body: JSON.stringify({ ids }),
        }
      );
    },

    async getTrash() {
      return requestTrash(request);
    },

    async restoreSubmission(id: string) {
      return request<{ success: boolean; message: string }>(
        `/submissions/${id}/restore`,
        {
          method: 'POST',
        }
      );
    },

    async bulkRestoreSubmissions(ids: string[]) {
      return request<{ success: boolean; restored: number; message: string }>(
        '/submissions/bulk-restore',
        {
          method: 'POST',
          body: JSON.stringify({ ids }),
        }
      );
    },

    async parkSubmission(id: string) {
      return request<{ submission: Submission }>(`/submissions/${id}`, {
        method: 'PUT',
        body: JSON.stringify({ status: 'PAUSED' as const }),
      });
    },

    async getServiceReportConfig() {
      return request<{ mode: 'CW' | 'WECLAPP' }>('/service-report/config');
    },

    async getDeliveryReportMode() {
      return request<{ mode: DeliveryReportMode }>('/config/delivery-report-mode');
    },

    /** Siehe `fetchSubmissionPdfBlob` — die Vorschau braucht den Blob statt der URL. */
    async getSubmissionPdfBlob(id: string): Promise<Blob> {
      return fetchSubmissionPdfBlob(id);
    },

    async downloadSubmissionPdf(id: string): Promise<void> {
      const blob = await fetchSubmissionPdfBlob(id);
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 30_000);
    },

    async searchWeclappTickets(search?: string, page?: number, pageSize?: number, customerId?: string) {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (page !== undefined) params.set('page', String(page));
      if (pageSize !== undefined) params.set('pageSize', String(pageSize));
      if (customerId) params.set('customerId', customerId);
      const query = params.toString() ? `?${params.toString()}` : '';
      return request<WeclappTicketListResponse>(`/weclapp/tickets${query}`);
    },
  };
}

async function requestTrash(request: RequestFn) {
  return request<{
    trash: (Submission & {
      daysUntilPermanentDeletion: number;
      user?: { displayName: string; email: string };
    })[];
    /** Aufbewahrung im Berichts-Papierkorb in Tagen (365) */
    retentionDays?: number;
  }>('/submissions/trash');
}
